import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '@features/auth/store/useSessionStore';
import DashboardTabs from '../components/shared/DashboardTabs';
import KpiStrip from '../components/kpi/KpiStrip';
import CallsOverTimeChart from '../components/charts/CallsOverTimeChart';
import DispositionPieChart from '../components/charts/DispositionPieChart';
import SdrLeaderboard from '../components/charts/SdrLeaderboard';
import AgentLeaderboard from '../components/charts/AgentLeaderboard';
import RecentSessionsTable from '../components/activity/RecentSessionsTable';
import FilterBar from '../components/FilterBar';
import useDashboardOverviewVM from '../hooks/useDashboardOverviewVM';
import useDashboardApi from '../hooks/useDashboardApi';
import { useDashboardStore } from '../store/useDashboardStore';
import type { OrganizationInfo, SdrInfo } from '../types/dashboard.types';

export default function DashboardOverviewPage (): React.JSX.Element {
  const navigate = useNavigate();
  const { currentUser } = useSessionStore();
  const dashboardApi = useDashboardApi();
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;
  const isTendrilUser = currentUser?.isTendrilUser === true;

  const {
    overview,
    recentSessions,
    loadingState,
    isRefreshing,
    granularity,
    prevPeriod,
    setGranularity,
  } = useDashboardOverviewVM();

  /**
   * Coming back to the overview ends the Active Sessions drill-down.
   *
   * It is a way of arriving at the sessions list, not a filter the user chose,
   * so it should not still be applied the next time they open that tab by
   * hand. Clearing on arrival here rather than on leaving the list means
   * opening a session's detail and coming back keeps the filter, which is what
   * you want mid-investigation.
   */
  useEffect(() => {
    useDashboardStore.getState().clearActiveSessions();
  }, []);

  /**
   * Opens the sessions list showing exactly what the Active Sessions tile
   * counted — including the date range it clears, which is the whole reason
   * the tile needed a destination: measured in QA, 3 of the 4 active sessions
   * had started outside "Last 7 days", so the number had nowhere to land.
   */
  const handleActiveSessionsClick = useCallback((): void => {
    useDashboardStore.getState().showActiveSessions();
    navigate('/dashboard');
  }, [navigate]);

  // Load organizations and SDRs for the filter bar
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [sdrs, setSdrs] = useState<SdrInfo[]>([]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }

    const loadOrgs = async (): Promise<void> => {
      const response = await dashboardApi.getOrganizationsAsync();
      if (response.success && response.data !== undefined) {
        setOrganizations(response.data);

        // Default to "Tendril" org if no org is selected
        const { filters, setFilters } = useDashboardStore.getState();
        if ((filters.orgIds?.length ?? 0) === 0) {
          const tendrilOrg = response.data.find(
            (org) => org.name.toLowerCase().includes('tendril'),
          );
          if (tendrilOrg !== undefined && currentUser?.isTendrilUser === true) {
            setFilters({ orgIds: [tendrilOrg.id] });
          }
        }
      }
    };

    void loadOrgs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  useEffect(() => {
    if (currentUser === null) {
      return;
    }

    const loadSdrs = async (): Promise<void> => {
      const response = await dashboardApi.getSdrsAsync(
        currentUser.userId,
        null,
        currentUser.organizationId,
      );
      if (response.success && response.data !== undefined) {
        setSdrs(response.data);
      }
    };

    void loadSdrs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId, currentUser?.organizationId]);

  const searchOrganizations = useCallback((query: string): void => {
    void (async () => {
      const response = await dashboardApi.getOrganizationsAsync(query);
      if (response.success && response.data !== undefined) {
        setOrganizations(response.data);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The picker holds one bounded page, so typing has to query the server —
  // the SDR being looked for may not be in the first 50.
  const handleSdrSearch = useCallback((query: string): void => {
    if (currentUser === null) {
      return;
    }

    void (async () => {
      const response = await dashboardApi.getSdrsAsync(
        currentUser.userId,
        null,
        currentUser.organizationId,
        query,
      );
      if (response.success && response.data !== undefined) {
        setSdrs(response.data);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.userId, currentUser?.organizationId]);

  const isLoading = loadingState === 'LOADING' || loadingState === 'IDLE' || overview === null;

  const handleViewAllSessions = (): void => {
    navigate('/dashboard');
  };

  const handleSessionClick = (sessionId: string): void => {
    navigate(`/dashboard/session/${sessionId}`);
  };

  return (
    <div className="space-y-4">
      {/* Header: Tabs */}
      <DashboardTabs />

      {/* Filters */}
      <FilterBar
        isSuperAdmin={isSuperAdmin}
        organizations={organizations}
        sdrs={sdrs}
        onSdrSearch={handleSdrSearch}
        onOrganizationSearch={searchOrganizations}
      />

      {/* Everything below the filters dims while a refresh is in flight,
          instead of the whole page collapsing into skeletons. The filter bar
          stays outside so its inputs never lose focus mid-typing. Dimming is
          opacity only — the controls inside remain clickable. */}
      <div
        className={`space-y-4 transition-opacity duration-200 ${
          isRefreshing ? 'opacity-60' : 'opacity-100'
        }`}
        aria-busy={isRefreshing}
      >
        {/* KPI Strip */}
        <KpiStrip
          kpis={overview?.kpis ?? null}
          isLoading={isLoading}
          prevPeriod={prevPeriod}
          onActiveSessionsClick={handleActiveSessionsClick}
        />

        {/* Charts Row 1: Calls Over Time + Disposition Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <CallsOverTimeChart
            data={overview?.callsOverTime ?? []}
            granularity={granularity}
            onGranularityChange={setGranularity}
            isLoading={isLoading}
          />
          <DispositionPieChart
            data={overview?.dispositionBreakdown ?? []}
            isLoading={isLoading}
          />
        </div>

        {/* Charts Row 2: the leaderboards. They replaced the pipeline funnel,
            which showed one conversion for the whole org and so could never say
            WHO turned their transfers into appointments.

            Agents are Tendril's own staff, so that board is only meaningful —
            and only shown — to Tendril users. When both are on screen they sit
            side by side; on their own, the SDR board takes the full width. */}
        <div
          className={
            isTendrilUser
              ? 'grid grid-cols-1 lg:grid-cols-2 gap-4'
              : 'grid grid-cols-1 gap-4'
          }
        >
          <SdrLeaderboard
            data={overview?.sdrLeaderboard ?? []}
            isLoading={isLoading}
          />
          {isTendrilUser && (
            <AgentLeaderboard
              data={overview?.agentLeaderboard ?? []}
              isLoading={isLoading}
            />
          )}
        </div>

        {/* Recent Sessions */}
        <RecentSessionsTable
          sessions={recentSessions}
          isLoading={isLoading}
          onViewAll={handleViewAllSessions}
          onSessionClick={handleSessionClick}
        />
      </div>
    </div>
  );
}
